/**
 * Pure step logic for the "Pre-flight" campaign editor (KampagneEditPage).
 * Five main steps (To, From, Subject, Content, Send time) must be
 * `complete` before the pre-send checks count, and the checks must pass
 * before Send unlocks. Nothing here talks to the API — the page feeds in
 * what it already has (form state, the audience estimate, the sender, the
 * last `/preflight` result) so the rules can be tested on their own.
 *
 * Send unlocking here only opens the way to KampagneSendPage; approval,
 * the typed recipient count and TOTP stay there, unchanged.
 */
import { validateBlocks, type Block } from './blocks';
import { formatNumber, formatSendTime } from './format';
import type { PreflightCheck } from '../types/api';

export type StepId = 'to' | 'from' | 'subject' | 'content' | 'time' | 'checks' | 'send';
export type StepState = 'complete' | 'warning' | 'incomplete' | 'locked' | 'running';
export type StepTag = 'Missing' | 'Check' | 'Fix' | 'Rerun';

export interface StepInfo {
  state: StepState;
  summary: string;
  tag?: StepTag | undefined;
}

export const MAIN_STEPS = ['to', 'from', 'subject', 'content', 'time'] as const;
export const ALL_STEPS: StepId[] = [...MAIN_STEPS, 'checks', 'send'];

export const STEP_LABELS: Record<StepId, string> = {
  to: 'To',
  from: 'From',
  subject: 'Subject',
  content: 'Content',
  time: 'Send time',
  checks: 'Checks',
  send: 'Send',
};

/** Soft limits shown as counters; going over is a warning, never a hard stop in the input. */
export const SUBJECT_MAX = 60;
/** Hard limit, same as the API's `PREHEADER_MAX_LENGTH`; the input stops there. */
export const PREHEADER_MAX = 150;

/**
 * `idle` = never run for this content; `stale` = content changed since the
 * last run; `error` = the request itself failed.
 */
export type ChecksPhase = 'idle' | 'running' | 'pass' | 'fail' | 'stale' | 'error';

export interface ChecksState {
  phase: ChecksPhase;
  checks: PreflightCheck[];
}

export interface EditorSnapshot {
  listNames: string[];
  /** `null` while the estimate is loading. */
  estimate: number | null;
  estimateError: boolean;
  sender: { fromName: string; fromEmail: string; verified: boolean } | null;
  subject: string;
  abEnabled: boolean;
  subjectB: string;
  /**
   * K6: content B (HTML) of an A/B test; empty/absent = B gets A's content.
   * An A/B test needs subject B or content B (or both).
   */
  htmlB?: string;
  editorMode: 'loading' | 'blocks' | 'html';
  blocks: Block[];
  html: string;
  checks: ChecksState;
  /**
   * `later` = the Schedule choice is on; the time may still be empty (`scheduledAt` null).
   * `local` = send at a local time per recipient's country (see `localSend`).
   * `smart` = like `later`, but each contact gets it at their usual hour inside a 24 h window from the chosen time.
   */
  sendMode: 'now' | 'later' | 'local' | 'smart';
  /** The local-time choice (only read when `sendMode` is `local`). */
  localSend?: { date: string; time: string; fallbackTz: string };
  /** The chosen send time in UTC epoch ms, or `null` (none chosen yet, or right after approval). */
  scheduledAt: number | null;
  /** Injected so the step is a pure function of its input. */
  now: number;
  /** The browser's IANA zone name, shown next to the time. */
  timeZone: string;
}

/** Same bound as the API (`SCHEDULE_MAX_AHEAD_MS` in packages/core): one year ahead at most. */
const SCHEDULE_MAX_AHEAD_MS = 365 * 24 * 60 * 60 * 1000;

/** What is wrong with a chosen send time, if anything. The API is strict: it must be in the future. */
/** True when the browser knows the IANA zone name (the API checks it again). */
export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export function checkSchedule(at: number, now: number): 'past' | 'far' | null {
  if (at <= now) return 'past';
  if (at > now + SCHEDULE_MAX_AHEAD_MS) return 'far';
  return null;
}

export function missingAltCount(blocks: Block[]): number {
  return blocks.filter((b) => b.type === 'image' && !b.alt.trim()).length;
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${formatNumber(n)} ${n === 1 ? one : many}`;
}

export function toStep(s: EditorSnapshot): StepInfo {
  if (s.listNames.length === 0) {
    return { state: 'incomplete', summary: 'Choose who gets this', tag: 'Missing' };
  }
  const lists = s.listNames.join(', ');
  if (s.estimateError) {
    return {
      state: 'warning',
      summary: `${lists}, recipients could not be counted`,
      tag: 'Check',
    };
  }
  if (s.estimate === null) return { state: 'complete', summary: `${lists}, counting recipients…` };
  if (s.estimate === 0) {
    return { state: 'warning', summary: `${lists}, no one matches`, tag: 'Check' };
  }
  return {
    state: 'complete',
    summary: `${lists}, ${plural(s.estimate, 'recipient')} (estimate)`,
  };
}

export function fromStep(s: EditorSnapshot): StepInfo {
  if (!s.sender) return { state: 'incomplete', summary: 'Select a sender', tag: 'Missing' };
  const who = `${s.sender.fromName} <${s.sender.fromEmail}>`;
  if (!s.sender.verified) {
    return { state: 'warning', summary: `${who}, domain not verified`, tag: 'Check' };
  }
  return { state: 'complete', summary: who };
}

export function subjectStep(s: EditorSnapshot): StepInfo {
  if (!s.subject.trim()) {
    return { state: 'incomplete', summary: 'Write a subject line', tag: 'Missing' };
  }
  if (s.subject.length > SUBJECT_MAX) {
    return { state: 'warning', summary: 'Long subject, may be cut off', tag: 'Check' };
  }
  const hasContentB = (s.htmlB ?? '').trim() !== '';
  if (s.abEnabled && !s.subjectB.trim() && !hasContentB) {
    return { state: 'warning', summary: 'Subject line B is empty', tag: 'Check' };
  }
  if (s.abEnabled && s.subjectB.length > SUBJECT_MAX) {
    return { state: 'warning', summary: 'Subject line B is long, may be cut off', tag: 'Check' };
  }
  const abLabel = !s.abEnabled
    ? ''
    : hasContentB
      ? s.subjectB.trim()
        ? 'A/B (subject + content): '
        : 'A/B (content): '
      : 'A/B: ';
  return { state: 'complete', summary: abLabel + s.subject };
}

export function contentStep(s: EditorSnapshot): StepInfo {
  if (s.editorMode === 'loading') return { state: 'incomplete', summary: 'Loading content…' };
  if (s.editorMode === 'html') {
    return s.html.trim()
      ? { state: 'complete', summary: 'HTML content' }
      : { state: 'incomplete', summary: 'Add some content', tag: 'Missing' };
  }
  const n = s.blocks.length;
  const body = s.blocks.filter((b) => b.type !== 'footer').length;
  if (body === 0) return { state: 'incomplete', summary: 'Add some content', tag: 'Missing' };
  const missingAlt = missingAltCount(s.blocks);
  if (missingAlt > 0) {
    return {
      state: 'warning',
      summary: `${plural(n, 'block')}, ${plural(missingAlt, 'image')} without alt text`,
      tag: 'Fix',
    };
  }
  const [firstError] = validateBlocks(s.blocks);
  if (firstError) return { state: 'warning', summary: firstError, tag: 'Fix' };
  return { state: 'complete', summary: plural(n, 'block') };
}

/** Right after approval, or at a chosen time (which must still be ahead and within the API's bound). */
export function timeStep(s: EditorSnapshot): StepInfo {
  if (s.sendMode === 'local') {
    const l = s.localSend ?? { date: '', time: '', fallbackTz: '' };
    if (!l.date || !l.time) {
      return { state: 'incomplete', summary: 'Pick a date and a local time', tag: 'Missing' };
    }
    if (l.fallbackTz.trim() && !isValidTimeZone(l.fallbackTz.trim())) {
      return { state: 'warning', summary: 'Unknown fallback time zone', tag: 'Fix' };
    }
    return { state: 'complete', summary: `${l.date} at ${l.time}, in each recipient's time zone` };
  }
  if (s.sendMode === 'now') return { state: 'complete', summary: 'Right after approval' };
  if (s.scheduledAt === null) {
    return { state: 'incomplete', summary: 'Pick a date and time', tag: 'Missing' };
  }
  const problem = checkSchedule(s.scheduledAt, s.now);
  if (problem === 'past') {
    return { state: 'warning', summary: 'The time has passed', tag: 'Fix' };
  }
  if (problem === 'far') {
    return { state: 'warning', summary: 'Too far ahead', tag: 'Fix' };
  }
  if (s.sendMode === 'smart') {
    return {
      state: 'complete',
      summary: `Smart send time, window starts ${formatSendTime(s.scheduledAt, s.timeZone)}`,
    };
  }
  return { state: 'complete', summary: formatSendTime(s.scheduledAt, s.timeZone) };
}

export function checksStep(checks: ChecksState): StepInfo {
  const total = checks.checks.length;
  const fails = checks.checks.filter((c) => c.status === 'fail').length;
  const warns = checks.checks.filter((c) => c.status === 'warn').length;
  switch (checks.phase) {
    case 'running':
      return { state: 'running', summary: 'Running…' };
    case 'pass':
      return {
        state: 'complete',
        summary:
          warns > 0
            ? `${formatNumber(total - warns)} of ${formatNumber(total)} passed, ${plural(warns, 'warning')}`
            : `All ${formatNumber(total)} passed`,
      };
    case 'fail':
      return { state: 'warning', summary: `${plural(fails, 'issue')} to fix`, tag: 'Fix' };
    case 'stale':
      return { state: 'incomplete', summary: 'Content changed, run again', tag: 'Rerun' };
    case 'error':
      return { state: 'warning', summary: 'The checks could not run', tag: 'Rerun' };
    case 'idle':
      return {
        state: 'incomplete',
        summary: 'Links, unsubscribe, alt text, size and sender',
      };
  }
}

export function computeSteps(s: EditorSnapshot): Record<StepId, StepInfo> {
  const main = {
    to: toStep(s),
    from: fromStep(s),
    subject: subjectStep(s),
    content: contentStep(s),
    time: timeStep(s),
  };
  // The checks only count once all five steps are ready (a run in flight still shows as running).
  const checks: StepInfo =
    mainReady(main) || s.checks.phase === 'running'
      ? checksStep(s.checks)
      : { state: 'locked', summary: 'Unlocks when all five steps are ready' };
  const ready = isReady(main, s.checks.phase);
  return {
    ...main,
    checks,
    send: ready
      ? { state: 'incomplete', summary: 'Send a test, then continue to approval' }
      : { state: 'locked', summary: 'Unlocks when every step above is green' },
  };
}

export function readyCount(steps: Pick<Record<StepId, StepInfo>, (typeof MAIN_STEPS)[number]>) {
  return MAIN_STEPS.filter((id) => steps[id].state === 'complete').length;
}

export function mainReady(
  steps: Pick<Record<StepId, StepInfo>, (typeof MAIN_STEPS)[number]>,
): boolean {
  return readyCount(steps) === MAIN_STEPS.length;
}

/** Send unlocks only when all five steps are complete and the last check run passed. */
export function isReady(
  steps: Pick<Record<StepId, StepInfo>, (typeof MAIN_STEPS)[number]>,
  checksPhase: ChecksPhase,
): boolean {
  return mainReady(steps) && checksPhase === 'pass';
}

export interface NextTarget {
  go: StepId;
  label: string;
  sub: string;
}

export function nextTarget(steps: Record<StepId, StepInfo>, checksPhase: ChecksPhase): NextTarget {
  const next = MAIN_STEPS.find((id) => steps[id].state !== 'complete');
  if (next) {
    return {
      go: next,
      label: `Next: ${STEP_LABELS[next]}`,
      sub: 'Checks and sending unlock when all five steps are ready.',
    };
  }
  if (checksPhase !== 'pass') {
    return {
      go: 'checks',
      label: checksPhase === 'fail' ? 'Next: Fix checks' : 'Next: Run checks',
      sub: 'All five steps are ready. Run the pre-send checks to unlock sending.',
    };
  }
  return {
    go: 'send',
    label: 'Next: Send',
    sub: 'Ready for sending. Send a test first if you want one more look.',
  };
}

/** Which step a failing `/preflight` check is fixed in (check ids from packages/core preflight). */
export function stepForCheck(checkId: string): StepId {
  switch (checkId) {
    case 'sender-verified':
      return 'from';
    case 'subject':
    case 'ab-test':
      return 'subject';
    default:
      // K6: content B is edited in the A/B section of the subject step.
      return checkId.startsWith('content-b-') ? 'subject' : 'content';
  }
}

/** One-line summary of a block for its collapsed row in the content step. */
export function blockSummary(block: Block): string {
  switch (block.type) {
    case 'text':
      return block.text.trim() || 'Empty text';
    case 'image': {
      if (block.alt.trim()) return `Alt: ${block.alt}`;
      const file = (block.src.split(/[?#]/)[0] ?? '').split('/').filter(Boolean).pop() ?? '';
      return `${file || 'No image'}, no alt text`;
    }
    case 'button':
      return block.label.trim() || 'No label';
    case 'product':
      return [block.name, block.price].filter((v) => v.trim()).join(', ') || 'No product details';
    case 'divider':
      return 'Thin line';
    case 'countdown':
      return block.endsOn.trim() || 'No end date';
    case 'coupon':
      return block.code.trim() || 'No code';
    case 'reviews':
      return block.quote.trim() || 'No quote';
    case 'video':
      return block.alt.trim() ? `Alt: ${block.alt}` : 'Video link, no alt text';
    case 'shared':
      return block.sharedBlockId ? block.name || 'Shared block' : 'No block chosen';
    case 'products':
      return block.feed.source === 'picked'
        ? `${block.feed.ids.length} hand-picked`
        : `${block.feed.count} ${block.feed.source === 'best' ? 'best sellers' : 'newest'}`;
    case 'footer':
      return block.companyAddress.trim()
        ? `Unsubscribe link, ${block.companyAddress}`
        : 'Sender details missing';
  }
}
